import { useCallback, useMemo, useRef } from 'react';

import { UserCommunication, ViewType } from '@this+that/models';
import log from 'loglevel';

import { useFetchIndividualUserCommunications } from './UserCommunications/fetch/useFetchIndividualUserCommunications';
import { useInboxAccountFilter } from './useInboxAccountFilter';
import { useInboxPagination } from './useInboxPagination';
import { useIntegrationsStore, useUserCommunicationsStore } from '../store';
import { INBOX_FEATURES_LIMIT } from '../utils/constants';

interface UseInboxPagePaginationProps {
  filteredCommunications: UserCommunication[];
  viewType: ViewType;
}

interface UseInboxPagePaginationReturn {
  currentPage: number;
  totalPages: number;
  paginatedItems: UserCommunication[];
  onPageChange: (page: number) => void;
  onPreviousPage: () => void;
  onNextPage: () => void;
}

export function useInboxPagePagination({
  filteredCommunications,
  viewType,
}: UseInboxPagePaginationProps): UseInboxPagePaginationReturn {
  const { isLoading } = useUserCommunicationsStore();

  const { integrationAccounts } = useIntegrationsStore();
  const { fetchEmailFilters } = useInboxAccountFilter(integrationAccounts);

  const {
    handleFetchIndividualUserCommunications,
    getHasMore: getHasMoreIndividual,
  } = useFetchIndividualUserCommunications({ limit: INBOX_FEATURES_LIMIT });

  const pagination = useInboxPagination({
    items: filteredCommunications,
  });

  const isFetchingRef = useRef<boolean>(false);

  const handlePageChange = useCallback(
    (page: number) => {
      pagination.onPageChange(page);
    },
    [pagination],
  );

  const handlePreviousPage = useCallback(() => {
    pagination.onPreviousPage();
  }, [pagination]);

  const handleNextPage = useCallback(async () => {
    const isLastPage = pagination.currentPage === pagination.totalPages;

    const hasMoreItems = getHasMoreIndividual(viewType as ViewType);

    const shouldFetch =
      isLastPage && hasMoreItems && !isLoading && !isFetchingRef.current;

    if (shouldFetch) {
      isFetchingRef.current = true;

      try {
        await handleFetchIndividualUserCommunications(
          viewType as ViewType,
          true,
          fetchEmailFilters,
        );
      } catch (error) {
        log.error('Error fetching more communications:', error);
      } finally {
        isFetchingRef.current = false;
      }
    }
    pagination.onNextPage();
  }, [
    viewType,
    pagination,
    getHasMoreIndividual,
    isLoading,
    handleFetchIndividualUserCommunications,
    fetchEmailFilters,
  ]);

  return useMemo(
    () => ({
      currentPage: pagination.currentPage,
      totalPages: pagination.totalPages,
      paginatedItems: pagination.paginatedItems,
      onPageChange: handlePageChange,
      onPreviousPage: handlePreviousPage,
      onNextPage: handleNextPage,
    }),
    [
      pagination.currentPage,
      pagination.totalPages,
      pagination.paginatedItems,
      handlePageChange,
      handlePreviousPage,
      handleNextPage,
    ],
  );
}
